import React, { useCallback, useEffect } from "react";

import { observer, useLocalObservable } from "mobx-react-lite";
import { IconEmailLight, skinVars } from "@telefonica/mistica";

import { StatusRequestPersonalContactsEmails, Store } from "./store";
import { strings } from "../../../localization";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { ValidationTypeOTP } from "../../../enum/validationTypeOTP";
import { ValidationOTPToken } from "../../../components/templates";
import { useAnalytics, useNavigateCustom } from "../../../hooks";

const ValidateTokenOTPByEmail: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { validationOTPStore, fintechRegisterStore, authenticationStore } = useGlobalStore();
	const [codeOTP, setCodeOTP] = React.useState("");
	const { title, description } = strings.pages.validateTokenOTPByEmail;
	const store = useLocalObservable(() => new Store(authenticationStore, fintechRegisterStore, validationOTPStore));
	const {updatePageView, addAnalyticEvent} = useAnalytics();
	const { buttonPrimary, buttonSecondary, resendCode } = strings.components.buttons;
	const { errorField} = strings.organisms.pinInput;

	const handleAnalyticEvent = (eventDescription: string): void => {
		addAnalyticEvent({
			component: "button",
			action: "click.button",
			step: eventDescription === resendCode ? "fgts:cadastro:confirmar-codigo:reenvio" : "fgts:cadastro:confirmar-codigo",
			description: eventDescription,
			event: "interaction",
		}, {applySanitize: true});
	};

	const sendOTPCode = useCallback(() => {
		setCodeOTP("");
		validationOTPStore.resetRemainingAttempts();
		validationOTPStore.sendValidationOTP(ValidationTypeOTP.EMAIL);
	}, [codeOTP, validationOTPStore.resetRemainingAttempts, validationOTPStore.sendValidationOTP]);

	const onBack = (shouldTriggerEvent?: boolean): void => {
		if (shouldTriggerEvent) {
			handleAnalyticEvent(buttonSecondary);
		}
		navigate("/fintech-registration/user-registration-data", { replace: true });
	};

	const handleContinue = async (): Promise<void> => {
		handleAnalyticEvent(buttonPrimary);
		const { isValid } = await validationOTPStore.validateOTP(codeOTP);
		let status: StatusRequestPersonalContactsEmails = StatusRequestPersonalContactsEmails.SUCCESS;
		// validado com sucesso
		if (isValid) {
			// envia email para ser cadastrado ou atualizado
			// verifica se o email é diferente do email principal
			const { email, mainEmail, newEmail } = fintechRegisterStore;

			if (mainEmail.value !== email.field.value && mainEmail.value !== newEmail.value) {
				status = await store.fetchEmail();
			}

			if (status !== StatusRequestPersonalContactsEmails.SUCCESS) {
				return;
			}

			return navigate("/fintech-registration/register-success", { state: { isCreated: false }, replace: true });
		}
	};

	useEffect(() => {
		updatePageView({
			name: "Vivo Money Cadastro FGTS Codigo Enviado",
			pagePath: "/cadastro-fgts/confirmar-codigo",
		});
		sendOTPCode();
	}, []);

	React.useEffect(() => {
		if (validationOTPStore.remainingAttempts < 3) {
			addAnalyticEvent({
				component: "button",
				action: "view-error",
				step: "fgts:cadastro:confirmar-codigo",
				description: errorField(validationOTPStore.remainingAttempts),
				event: "non_interaction",
			}, {applySanitize: true});
		}
	}, [validationOTPStore.remainingAttempts]);

	return (
		<ValidationOTPToken
			title={title}
			description={description}
			isDisabled={
				validationOTPStore.loader.isLoading || validationOTPStore.remainingAttempts <= 0 || store.loader.isLoading
			}
			isDisabledInput={validationOTPStore.remainingAttempts <= 0}
			codeOTP={codeOTP}
			icon={<IconEmailLight size={64} color={skinVars.colors.brand} />}
			attempts={validationOTPStore.remainingAttempts}
			setCodeOtp={(t) => setCodeOTP(t)}
			onBack={onBack}
			onResend={() => {
				sendOTPCode();
				handleAnalyticEvent(resendCode);
			}}
			onContinue={handleContinue}
		/>
	);
});

export default ValidateTokenOTPByEmail;
